// Pylon attaches a command function as `window.Pylon`. We access it through a
// narrow structural type rather than `any` so property accesses stay
// type-checked. The widget's config (`window.pylon.chat_settings`, including
// the logged-in user's identity and verification `email_hash`) is rendered
// server-side in `views/pylon.erb`.
// The inline loader (views/pylon.erb) sets `window.Pylon` to a command-queue
// stub with a `q` array immediately; the async widget script replaces it with
// the real function (without `q`) once it loads. `q` is what distinguishes the
// stub from the loaded widget.
type PylonFn = ((method: string, ...args: unknown[]) => void) & {
  q?: unknown[]
}

interface PylonWindow {
  Pylon?: PylonFn
}

function getPylonWindow(): PylonWindow {
  return window as unknown as PylonWindow
}

// Whether the real Pylon widget loaded. The mere presence of `window.Pylon`
// only means the inline stub ran, so check that the command queue (`q`) is gone
// — the loaded widget replaces the stub. Used to confirm the widget actually
// resolved for a logged-in user rather than silently failing to load.
export function isPylonLoaded(): boolean {
  const { Pylon } = getPylonWindow()
  return Pylon !== undefined && Pylon.q === undefined
}

export function showNewPylonMessage(message?: string): boolean {
  const { Pylon } = getPylonWindow()
  if (Pylon === undefined) {
    return false
  }
  Pylon("showNewMessage", message)
  return true
}

export function showPylon(): boolean {
  const { Pylon } = getPylonWindow()
  if (Pylon === undefined) {
    return false
  }
  Pylon("show")
  return true
}

export function hidePylon(): boolean {
  const { Pylon } = getPylonWindow()
  if (Pylon === undefined) {
    return false
  }
  Pylon("hide")
  return true
}

export interface PylonVisibilityHandlers {
  onShow: () => void
  onHide: () => void
}

// Registers callbacks fired when the chat window opens/closes, so callers can
// reflect the widget's own state (including the user closing it via its X).
// Returns a cleanup that removes the handlers; Pylon unregisters a callback
// when passed null. No-ops when the widget hasn't loaded.
export function subscribePylonVisibility(handlers: PylonVisibilityHandlers): () => void {
  const { Pylon } = getPylonWindow()
  if (Pylon === undefined) {
    return () => {}
  }
  Pylon("onShow", handlers.onShow)
  Pylon("onHide", handlers.onHide)
  return () => {
    const { Pylon: current } = getPylonWindow()
    current?.("onShow", null)
    current?.("onHide", null)
  }
}

// Registers a callback fired whenever the unread message count changes, so
// callers can surface an unread badge. Returns a cleanup that unregisters the
// handler (Pylon unregisters when passed null). No-ops when the widget hasn't
// loaded.
export function subscribePylonUnreadCount(handler: (count: number) => void): () => void {
  const { Pylon } = getPylonWindow()
  if (Pylon === undefined) {
    return () => {}
  }
  Pylon("onChangeUnreadMessagesCount", handler)
  return () => {
    const { Pylon: current } = getPylonWindow()
    current?.("onChangeUnreadMessagesCount", null)
  }
}
